/**
 * Passos do onboarding. "novo": setor → segmento → cliente → local → origem (sem resumo desde
 * 27/10/2026: a última pergunta conclui).
 * "completar" (quem já respondeu o questionário antigo): só o que falta, com o resto preenchido.
 */
import type { MeProfile } from '../../types/session';
import { customerByLegacy, isReach, sectorByIndustry } from './catalog';

export type QuestionStep = 'setor' | 'segmento' | 'cliente' | 'local' | 'origem';
export type GroupId = 'what' | 'who' | 'where' | 'origin';

export const GROUP_OF: Record<QuestionStep, GroupId> = {
  setor: 'what',
  segmento: 'what',
  cliente: 'who',
  local: 'where',
  origem: 'origin',
};

export type OnboardingMode = 'novo' | 'completar';

export interface Flow {
  steps: QuestionStep[];
  start: QuestionStep;
  groups: GroupId[];
}

export function planFlow(mode: OnboardingMode, profile: MeProfile | undefined): Flow {
  if (mode === 'novo') {
    const steps: QuestionStep[] = ['setor', 'segmento', 'cliente', 'local', 'origem'];
    return { steps, start: 'setor', groups: ['what', 'who', 'where', 'origin'] };
  }
  const sector = sectorByIndustry(profile?.industry);
  const needsWhat = !profile?.segment || !sector;
  const steps: QuestionStep[] = [];
  if (needsWhat) steps.push('setor', 'segmento');
  if (!customerByLegacy(profile?.customer)) steps.push('cliente');
  if (!isReach(profile?.reach)) steps.push('local');
  // Nada a perguntar (perfil completo por outro caminho): pelo menos confirma o alcance.
  if (steps.length === 0) steps.push('local');
  const first = steps[0] ?? 'local';
  const start: QuestionStep = first === 'setor' && sector ? 'segmento' : first;
  const groups = steps.map((s) => GROUP_OF[s]).filter((g, i, all) => all.indexOf(g) === i);
  return { steps, start, groups };
}

/** Número do passo (1…total) mostrado no topo. */
export function stepNumber(flow: Flow, step: QuestionStep): number {
  return flow.groups.indexOf(GROUP_OF[step]) + 1;
}

export function nextStep(flow: Flow, step: QuestionStep): QuestionStep | null {
  const i = flow.steps.indexOf(step);
  return i >= 0 && i < flow.steps.length - 1 ? (flow.steps[i + 1] ?? null) : null;
}

/**
 * Passo anterior. Volta também para antes do início: no "completar" que abre no segmento (setor
 * já conhecido), "Voltar" leva ao setor, que é o jeito de trocar de setor desde que saiu o
 * "Trocar setor" (27/10/2026).
 */
export function prevStep(flow: Flow, step: QuestionStep): QuestionStep | null {
  const i = flow.steps.indexOf(step);
  return i > 0 ? (flow.steps[i - 1] ?? null) : null;
}
